import { useEffect, useRef, useState } from 'react'
import {
  Bot,
  Check,
  ImagePlus,
  LayoutGrid,
  Loader2,
  Palette,
  RotateCcw,
  Sparkles,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  useTenantSettings,
  useUpdateTenantSettings,
  useUploadCompanyLogo,
} from '@/features/company/queries'
import { BRAND_PRESETS, DEFAULT_BRAND_HEX } from '@/features/branding/presets'
import { SURFACE_PRESETS, DEFAULT_SURFACE_HEX } from '@/features/branding/surfaces'
import {
  CARD_SURFACE_PRESETS,
  TEXT_COLOR_PRESETS,
  SIDEBAR_TEXT_PRESETS,
  SECONDARY_COLOR_PRESETS,
  DEFAULT_CARD_HEX,
  DEFAULT_TEXT_HEX,
  DEFAULT_SIDEBAR_HEX,
  DEFAULT_SECONDARY_HEX,
} from '@/features/branding/extras'
import { deriveBrandPalette } from '@/lib/color'
import { useDarkMode } from '@/components/charts/palette'
import { ColorPickerSection } from './ColorPickerSection'
import { useColorField } from './useColorField'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error || err.message || fallback
  }
  return err instanceof Error ? err.message : fallback
}

export function BrandingPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canEdit = hasPermission('tenant.settings.edit')
  // Surface / card / text customisations are deliberately ignored in
  // dark mode (TenantBrandSync clears those overrides), so the preview
  // blocks must match - otherwise we paint stale light-mode hexes onto
  // a dark canvas and the cards look broken (issue user flagged).
  const isDark = useDarkMode()

  const q = useTenantSettings()
  const update = useUpdateTenantSettings()
  const logoMutation = useUploadCompanyLogo()

  // ── Logo state (moved verbatim from Company Settings) ───────────────
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [pendingLogo, setPendingLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const serverLogo = q.data?.tenant?.logo_url || '/images/logo.png'
  const displayedLogo = logoPreview || serverLogo

  // ── Brand colour state ──────────────────────────────────────────────
  const savedHex = q.data?.tenant?.brand_color || null
  const [draftHex, setDraftHex] = useState<string>(savedHex || DEFAULT_BRAND_HEX)
  const [hexInput, setHexInput] = useState<string>(savedHex || DEFAULT_BRAND_HEX)

  useEffect(() => {
    const next = savedHex || DEFAULT_BRAND_HEX
    setDraftHex(next)
    setHexInput(next)
  }, [savedHex])

  const isValidHex = HEX_RE.test(hexInput.trim())
  const dirty = draftHex.toLowerCase() !== (savedHex || DEFAULT_BRAND_HEX).toLowerCase()
  const palette = deriveBrandPalette(draftHex)

  const onPickPreset = (hex: string) => {
    if (!canEdit) return
    setDraftHex(hex)
    setHexInput(hex)
  }

  const onHexChange = (v: string) => {
    setHexInput(v)
    if (HEX_RE.test(v.trim())) setDraftHex(v.trim().toLowerCase())
  }

  // ── Surface tint state (canvas / sidebar / header background) ───────
  const savedSurface = q.data?.tenant?.surface_tint || null
  const [draftSurface, setDraftSurface] = useState<string>(savedSurface || DEFAULT_SURFACE_HEX)
  const [surfaceInput, setSurfaceInput] = useState<string>(savedSurface || DEFAULT_SURFACE_HEX)

  useEffect(() => {
    const next = savedSurface || DEFAULT_SURFACE_HEX
    setDraftSurface(next)
    setSurfaceInput(next)
  }, [savedSurface])

  const isValidSurface = HEX_RE.test(surfaceInput.trim())
  const surfaceDirty =
    draftSurface.toLowerCase() !== (savedSurface || DEFAULT_SURFACE_HEX).toLowerCase()

  const onPickSurface = (hex: string) => {
    if (!canEdit) return
    setDraftSurface(hex)
    setSurfaceInput(hex)
  }

  const onSurfaceChange = (v: string) => {
    setSurfaceInput(v)
    if (HEX_RE.test(v.trim())) setDraftSurface(v.trim().toLowerCase())
  }

  const saveSurface = async () => {
    if (!isValidSurface) return
    try {
      await update.mutateAsync({ surface_tint: draftSurface.toLowerCase() })
      toast({ title: 'Surface tone updated.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to update surface tone',
        description: messageFor(err, ''),
      })
    }
  }

  const resetSurface = async () => {
    try {
      await update.mutateAsync({ surface_tint: null })
      toast({ title: 'Surface reset to default.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to reset surface tone',
        description: messageFor(err, ''),
      })
    }
  }

  const onLogoSelect = (file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ variant: 'destructive', title: 'Please choose an image file.' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ variant: 'destructive', title: 'Image must be 5 MB or smaller.' })
      return
    }
    setPendingLogo(file)
    const reader = new FileReader()
    reader.onload = (e) => setLogoPreview(String(e.target?.result ?? ''))
    reader.readAsDataURL(file)
  }

  const uploadLogo = async () => {
    if (!pendingLogo) return
    try {
      await logoMutation.mutateAsync(pendingLogo)
      setPendingLogo(null)
      setLogoPreview(null)
      if (logoInputRef.current) logoInputRef.current.value = ''
      toast({ title: 'Company logo updated.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to upload logo',
        description: messageFor(err, ''),
      })
    }
  }

  const cancelLogo = () => {
    setPendingLogo(null)
    setLogoPreview(null)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const saveColor = async () => {
    if (!isValidHex) return
    try {
      await update.mutateAsync({ brand_color: draftHex.toLowerCase() })
      toast({ title: 'Brand colour updated.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to update brand colour',
        description: messageFor(err, ''),
      })
    }
  }

  // ── Theme extras: card / text / sidebar text / secondary ────────────
  // Each colour follows the same "saved → draft → typed input" dance.
  // The `useColorField` hook captures it once so the BrandingPage stays
  // readable. Tenant column → CSS token mapping is mirrored in
  // TenantBrandSync.
  const cardField = useColorField({
    column: 'card_surface',
    saved: q.data?.tenant?.card_surface ?? null,
    defaultHex: DEFAULT_CARD_HEX,
    savedTitle: 'Card surface updated.',
    resetTitle: 'Card surface reset to default.',
  })
  const textField = useColorField({
    column: 'text_color',
    saved: q.data?.tenant?.text_color ?? null,
    defaultHex: DEFAULT_TEXT_HEX,
    savedTitle: 'Text colour updated.',
    resetTitle: 'Text colour reset to default.',
  })
  const sidebarTextField = useColorField({
    column: 'sidebar_text',
    saved: q.data?.tenant?.sidebar_text ?? null,
    defaultHex: DEFAULT_SIDEBAR_HEX,
    savedTitle: 'Sidebar text colour updated.',
    resetTitle: 'Sidebar text reset to default.',
  })
  const secondaryField = useColorField({
    column: 'secondary_color',
    saved: q.data?.tenant?.secondary_color ?? null,
    defaultHex: DEFAULT_SECONDARY_HEX,
    savedTitle: 'Secondary button colour updated.',
    resetTitle: 'Secondary colour reset to default.',
  })

  const resetLogo = async () => {
    try {
      await update.mutateAsync({ logo_url: null })
      setPendingLogo(null)
      setLogoPreview(null)
      if (logoInputRef.current) logoInputRef.current.value = ''
      toast({ title: 'Reverted to default Bokapsys logo.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to reset logo',
        description: messageFor(err, ''),
      })
    }
  }

  const resetColor = async () => {
    try {
      await update.mutateAsync({ brand_color: null })
      toast({ title: 'Reset to Bokapsys default.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to reset brand colour',
        description: messageFor(err, ''),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Page title="Branding">
        <Card className="p-12">
          <LoadingSpinner />
        </Card>
      </Page>
    )
  }

  return (
    <Page
      title="Branding"
      description="Upload your logo and pick the brand colour the app uses for buttons, links, and accents. Changes apply for every member of this workspace."
      width="default"
    >
      {/* ── Logo card ──────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>Company Logo</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Appears in the sidebar and on customer-facing emails, invoices, and the hosted-pay page.
              </p>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                onClick={resetLogo}
                disabled={
                  update.isPending ||
                  logoMutation.isPending ||
                  (!q.data?.tenant?.logo_url && !pendingLogo)
                }
                title={
                  q.data?.tenant?.logo_url
                    ? 'Revert to the default Bokapsys logo'
                    : 'Already using the default logo'
                }
              >
                <RotateCcw className="size-4" /> Use default
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <div className="flex h-24 w-48 items-center justify-center rounded-md border bg-muted/40 p-2">
              <img
                src={displayedLogo}
                alt="Company logo"
                onError={(e) => {
                  const img = e.currentTarget
                  if (img.src.indexOf('/images/logo.png') === -1) img.src = '/images/logo.png'
                }}
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Recommended: 400 × 120 px. PNG or SVG with a transparent background. Max 5 MB.
              </p>
              {canEdit ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onLogoSelect(e.currentTarget.files?.[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoMutation.isPending}
                  >
                    <ImagePlus className="size-4" /> Choose Logo
                  </Button>
                  {pendingLogo ? (
                    <>
                      <Button
                        type="button"
                        onClick={uploadLogo}
                        disabled={logoMutation.isPending}
                      >
                        {logoMutation.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : null}
                        Upload Logo
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={cancelLogo}
                        disabled={logoMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <span className="truncate text-xs text-muted-foreground">
                        {pendingLogo.name}
                      </span>
                    </>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  You don&rsquo;t have permission to change the company logo.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Colour card ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>Brand Colour</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Pick a preset or paste a hex value to match your company. Buttons, links, active
                tabs, charts, and the sidebar accent will follow.
              </p>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                onClick={resetColor}
                disabled={
                  update.isPending ||
                  (!savedHex && draftHex.toLowerCase() === DEFAULT_BRAND_HEX.toLowerCase())
                }
                title={savedHex ? 'Revert to Bokapsys blue' : 'Already using Bokapsys blue'}
              >
                <RotateCcw className="size-4" /> Use default
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Preset swatches */}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Presets</p>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {BRAND_PRESETS.map((p) => {
                const active = draftHex.toLowerCase() === p.hex.toLowerCase()
                return (
                  // Color swatches are inherently style-data, not text buttons -
                  // the wrapped <Button> primitive doesn't fit (its variants
                  // would override the per-tile backgroundColor we need to show
                  // the actual preset). Native <button> with full a11y wiring.
                  // eslint-disable-next-line no-restricted-syntax
                  <button
                    key={p.hex}
                    type="button"
                    onClick={() => onPickPreset(p.hex)}
                    disabled={!canEdit}
                    title={`${p.name} (${p.hex})`}
                    aria-label={p.name}
                    aria-pressed={active}
                    className={cn(
                      'group flex flex-col items-center gap-1.5 rounded-md p-1.5 text-xs transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                      active ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : 'hover:bg-accent',
                    )}
                  >
                    <span
                      className="relative flex size-10 items-center justify-center rounded-pill shadow-card"
                      style={{ backgroundColor: p.hex }}
                    >
                      {active ? (
                        <Check className="size-5 text-primary-foreground" aria-hidden />
                      ) : null}
                    </span>
                    <span className="line-clamp-1 font-medium text-foreground">{p.name}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Custom hex */}
          <div className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
            <FormField
              label="Custom hex"
              hint={
                <>
                  Six-character hex including the leading <code className="rounded bg-muted px-1">#</code>.
                  Hover and dark-tile shades are derived automatically.
                </>
              }
              control={
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={draftHex}
                    onChange={(e) => onHexChange(e.target.value)}
                    disabled={!canEdit}
                    aria-label="Open colour picker"
                    title="Click to open colour picker"
                    className={cn(
                      // Override the Input primitive's full-width / padded
                      // shape - for a colour swatch we want a 36px square
                      // with no inner padding so the swatch fills it.
                      'size-9 shrink-0 cursor-pointer rounded-md p-0',
                      // Strip the browser's default chrome so the colour
                      // bleeds to the rounded edge. Works in Chromium /
                      // Safari (-webkit-) + Firefox (-moz-).
                      '[&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-0',
                      '[&::-webkit-color-swatch-wrapper]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0',
                      '[&::-moz-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0',
                    )}
                  />
                  <Input
                    type="text"
                    value={hexInput}
                    onChange={(e) => onHexChange(e.target.value)}
                    placeholder="e.g. 0072CE"
                    spellCheck={false}
                    className="font-mono uppercase"
                    disabled={!canEdit}
                  />
                </div>
              }
            />
            <span className="hidden sm:block" />
            {canEdit ? (
              <Button
                type="button"
                onClick={saveColor}
                disabled={!dirty || !isValidHex || update.isPending}
              >
                {update.isPending ? <Loader2 className="size-4 animate-spin" /> : <Palette className="size-4" />}
                Save colour
              </Button>
            ) : null}
          </div>

          {/* Live preview */}
          {palette ? (
            <div
              className="rounded-lg border border-border p-5"
              style={{
                /*
                 * Scope the override to this preview block. The real
                 * tenant-wide override lives in TenantBrandSync and only
                 * fires after Save - this lets the user see the new look
                 * before committing without re-skinning every other tab.
                 */
                ['--bk-primary' as never]: palette.primary,
                ['--bk-primary-hover' as never]: palette.hover,
                ['--bk-primary-strong' as never]: palette.strong,
                ['--bk-accent' as never]: palette.accent,
                ['--bk-accent-fg' as never]: palette.accentFg,
              }}
            >
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Preview
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div
                  className="rounded-lg p-5 text-primary-foreground"
                  style={{ backgroundColor: `hsl(${palette.strong})` }}
                >
                  <p className="text-sm font-medium opacity-85">Total Income</p>
                  <p className="mt-3 text-3xl font-semibold leading-none tracking-tight">
                    $128,400
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 rounded-pill bg-white/15 px-2 py-0.5 text-xs font-medium">
                    +12% vs prev
                  </span>
                </div>
                <div className="flex flex-col justify-between rounded-lg bg-card p-5 shadow-card">
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm">Save changes</Button>
                    <Button type="button" variant="outline" size="sm">Cancel</Button>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 font-medium text-accent-foreground">
                      Active tab
                    </span>
                    <a className="text-primary underline-offset-2 hover:underline" href="#">
                      A primary link
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {!canEdit ? (
            <p className="text-xs text-muted-foreground">
              You don&rsquo;t have permission to change the brand colour.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Surface tone card ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>Surface Tone</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                The neutral background behind every page - sidebar, header, and the canvas
                cards float on. Pick a tone that complements your brand colour.
              </p>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                onClick={resetSurface}
                disabled={
                  update.isPending ||
                  (!savedSurface && draftSurface.toLowerCase() === DEFAULT_SURFACE_HEX.toLowerCase())
                }
                title={savedSurface ? 'Revert to default surface' : 'Already using the default'}
              >
                <RotateCcw className="size-4" /> Use default
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Presets</p>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
              {SURFACE_PRESETS.map((s) => {
                const active = draftSurface.toLowerCase() === s.hex.toLowerCase()
                return (
                  // Same justification as the brand-preset swatches above -
                  // these are inherently style-data tiles, not text buttons.
                  // eslint-disable-next-line no-restricted-syntax
                  <button
                    key={s.hex}
                    type="button"
                    onClick={() => onPickSurface(s.hex)}
                    disabled={!canEdit}
                    title={`${s.name} (${s.hex})`}
                    aria-label={s.name}
                    aria-pressed={active}
                    className={cn(
                      'group flex flex-col items-center gap-1.5 rounded-md p-1.5 text-xs transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                      active ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : 'hover:bg-accent',
                    )}
                  >
                    <span
                      className="relative flex size-10 items-center justify-center rounded-pill border border-border shadow-card"
                      style={{ backgroundColor: s.hex }}
                    >
                      {active ? <Check className="size-5 text-foreground" aria-hidden /> : null}
                    </span>
                    <span className="line-clamp-1 font-medium text-foreground">{s.name}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
            <FormField
              label="Custom hex"
              hint="Use a near-white or very pale neutral so the brand accent stays the visual focus."
              control={
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={draftSurface}
                    onChange={(e) => onSurfaceChange(e.target.value)}
                    disabled={!canEdit}
                    aria-label="Open surface colour picker"
                    title="Click to open colour picker"
                    className={cn(
                      'size-9 shrink-0 cursor-pointer rounded-md p-0',
                      '[&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-0',
                      '[&::-webkit-color-swatch-wrapper]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0',
                      '[&::-moz-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0',
                    )}
                  />
                  <Input
                    type="text"
                    value={surfaceInput}
                    onChange={(e) => onSurfaceChange(e.target.value)}
                    placeholder="e.g. F0F2F6"
                    spellCheck={false}
                    className="font-mono uppercase"
                    disabled={!canEdit}
                  />
                </div>
              }
            />
            <span className="hidden sm:block" />
            {canEdit ? (
              <Button
                type="button"
                onClick={saveSurface}
                disabled={!surfaceDirty || !isValidSurface || update.isPending}
              >
                {update.isPending ? <Loader2 className="size-4 animate-spin" /> : <Palette className="size-4" />}
                Save tone
              </Button>
            ) : null}
          </div>

          {/* Live preview - a chunk of canvas with a sidebar pill + a card sitting on it.
              In dark mode the inline backgroundColor is dropped so the
              preview falls through to the dark canvas token (matches the
              real app, which also ignores surface_tint in dark mode). */}
          {isValidSurface ? (
            <div
              className="rounded-lg border border-border p-5"
              style={isDark ? { backgroundColor: 'hsl(var(--bk-bg))' } : { backgroundColor: draftSurface }}
            >
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Preview {isDark ? <span className="ml-1 text-[11px] normal-case tracking-normal">(applies in light mode)</span> : null}
              </p>
              <div className="flex flex-wrap items-stretch gap-4">
                <div className="flex min-w-[180px] flex-col gap-0.5 p-2">
                  <SidebarPreviewItem icon={LayoutGrid} label="Dashboard" active />
                  <SidebarPreviewItem icon={Bot} label="Smart Chat" />
                  <SidebarPreviewItem icon={Sparkles} label="AI Agents" />
                  <SidebarPreviewItem icon={Workflow} label="Workflows" />
                  <SidebarPreviewItem icon={Wallet} label="Budget Planning" />
                </div>
                <div className="flex flex-1 items-center justify-center rounded-lg bg-card p-5 shadow-card">
                  <p className="text-sm text-muted-foreground">A card sitting on the canvas.</p>
                </div>
              </div>
            </div>
          ) : null}

          {!canEdit ? (
            <p className="text-xs text-muted-foreground">
              You don&rsquo;t have permission to change the surface tone.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Card Surface ────────────────────────────────────────────── */}
      <ColorPickerSection
        title="Card Surface"
        description="The colour every card, modal, dropdown, and the chat panel sit on. Pick a near-white shade that complements the canvas tone."
        presets={CARD_SURFACE_PRESETS}
        saved={q.data?.tenant?.card_surface ?? null}
        defaultHex={DEFAULT_CARD_HEX}
        draft={cardField.draft}
        hexInput={cardField.hexInput}
        canEdit={canEdit}
        busy={cardField.busy}
        onPick={cardField.onPick}
        onHexChange={cardField.onHexChange}
        onSave={cardField.onSave}
        onReset={cardField.onReset}
        preview={
          <div
            className="rounded-lg border border-border p-5"
            style={
              isDark
                ? { backgroundColor: 'hsl(var(--bk-card))' }
                : cardField.isValid
                  ? { backgroundColor: cardField.draft }
                  : undefined
            }
          >
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preview {isDark ? <span className="ml-1 text-[11px] normal-case tracking-normal">(applies in light mode)</span> : null}
            </p>
            <p className="text-sm text-foreground">A card sitting on the canvas.</p>
          </div>
        }
      />

      {/* ── Text Colour ─────────────────────────────────────────────── */}
      <ColorPickerSection
        title="Text Colour"
        description="The dark colour used for headings and body text across the app. Stay in the near-black family - extreme values can hurt readability."
        presets={TEXT_COLOR_PRESETS}
        saved={q.data?.tenant?.text_color ?? null}
        defaultHex={DEFAULT_TEXT_HEX}
        draft={textField.draft}
        hexInput={textField.hexInput}
        canEdit={canEdit}
        busy={textField.busy}
        onPick={textField.onPick}
        onHexChange={textField.onHexChange}
        onSave={textField.onSave}
        onReset={textField.onReset}
        preview={
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            {/* Lifted verbatim from the Dashboard's <Page title= description=> so
                the preview shows your text colour the way users will see it on
                the page they land on most. */}
            <p
              className="text-2xl font-semibold leading-tight"
              style={{ color: textField.isValid ? textField.draft : undefined }}
            >
              Overview
            </p>
            <p
              className="mt-1 text-sm"
              style={{ color: textField.isValid ? textField.draft : undefined }}
            >
              Financial snapshot with income, expenses, and category breakdowns for the selected period.
            </p>
          </div>
        }
      />

      {/* ── Sidebar Text Colour ─────────────────────────────────────── */}
      <ColorPickerSection
        title="Sidebar Text"
        description="The colour of inactive sidebar nav labels. Active items stay in your brand colour - this only affects the muted state."
        presets={SIDEBAR_TEXT_PRESETS}
        saved={q.data?.tenant?.sidebar_text ?? null}
        defaultHex={DEFAULT_SIDEBAR_HEX}
        draft={sidebarTextField.draft}
        hexInput={sidebarTextField.hexInput}
        canEdit={canEdit}
        busy={sidebarTextField.busy}
        onPick={sidebarTextField.onPick}
        onHexChange={sidebarTextField.onHexChange}
        onSave={sidebarTextField.onSave}
        onReset={sidebarTextField.onReset}
        preview={
          <div
            className="rounded-lg border border-border p-3"
            style={{ backgroundColor: 'hsl(var(--bk-sidebar-bg))' }}
          >
            <p className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            <div className="flex flex-col gap-0.5">
              <SidebarPreviewItem icon={LayoutGrid} label="Dashboard" active />
              <SidebarPreviewItem
                icon={Bot}
                label="Smart Chat"
                color={sidebarTextField.isValid ? sidebarTextField.draft : undefined}
              />
              <SidebarPreviewItem
                icon={Sparkles}
                label="AI Agents"
                color={sidebarTextField.isValid ? sidebarTextField.draft : undefined}
              />
              <SidebarPreviewItem
                icon={Workflow}
                label="Workflows"
                color={sidebarTextField.isValid ? sidebarTextField.draft : undefined}
              />
              <SidebarPreviewItem
                icon={Wallet}
                label="Budget Planning"
                color={sidebarTextField.isValid ? sidebarTextField.draft : undefined}
              />
            </div>
          </div>
        }
      />

      {/* ── Secondary Button Colour ─────────────────────────────────── */}
      <ColorPickerSection
        title="Secondary Button"
        description="The neutral background of secondary buttons (the gray ones, alongside the brand-coloured primary). Stay in a dark gray so the white text on top stays readable."
        presets={SECONDARY_COLOR_PRESETS}
        saved={q.data?.tenant?.secondary_color ?? null}
        defaultHex={DEFAULT_SECONDARY_HEX}
        draft={secondaryField.draft}
        hexInput={secondaryField.hexInput}
        canEdit={canEdit}
        busy={secondaryField.busy}
        onPick={secondaryField.onPick}
        onHexChange={secondaryField.onHexChange}
        onSave={secondaryField.onSave}
        onReset={secondaryField.onReset}
        preview={
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button">Primary</Button>
              <Button
                type="button"
                style={{
                  backgroundColor: secondaryField.isValid ? secondaryField.draft : undefined,
                }}
              >
                Secondary
              </Button>
              <Button type="button" variant="outline">Outline</Button>
            </div>
          </div>
        }
      />
    </Page>
  )
}

/**
 * Mini sidebar row used in the Surface Tone + Sidebar Text previews.
 * Mirrors the real <Sidebar /> active/inactive treatment so the user
 * sees what their pick will actually look like in the running app -
 * brand-coloured pill on the active row, the chosen muted colour on
 * the inactive ones.
 */
function SidebarPreviewItem({
  icon: Icon,
  label,
  active,
  color,
}: {
  icon: LucideIcon
  label: string
  active?: boolean
  /** Override colour for inactive rows; ignored when `active` is true. */
  color?: string
}) {
  return (
    <span
      className={cn(
        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium',
        active && 'bg-sidebar-accent font-semibold text-primary shadow-card',
      )}
      style={!active && color ? { color } : undefined}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  )
}
